const PLUS_BONUS_STORE_LIMIT = 1;

export function hasProPlan(retailers: string[]): boolean {
  return retailers.some(
    (r) => r.includes("_pro") || r.includes("_starter") || r.includes("_enterprise")
  );
}

export function hasPlusPlan(retailers: string[]): boolean {
  return retailers.some((r) => r.includes("_plus"));
}

export function hasPlusOrProPlan(retailers: string[]): boolean {
  return hasProPlan(retailers) || hasPlusPlan(retailers);
}

export function shouldPromptProStoreUpgrade(retailers: string[], retailer: string): boolean {
  if (!hasPlusPlan(retailers) || hasProPlan(retailers)) return false;
  return getRetailerStoreLimit(retailers, retailer) > 0;
}

// Retailers whose physical-store data is a Plus/Pro benefit even if a stray
// "<retailer>_basic" entitlement exists (mirrors the API's checkRetailerAccess).
const PLUS_ONLY_LOCAL_RETAILERS = new Set(["tractor_supply", "northern_tool", "samsclub", "ace_hardware"]);

function hasDirectRetailerPlan(retailers: string[], retailer: string): boolean {
  const slug = retailer.toLowerCase().trim();
  return retailers.some((plan) => {
    const value = plan.toLowerCase().trim();
    return value === slug || value.startsWith(`${slug}_`);
  });
}

/**
 * Store slots the user's plans grant for a retailer. Bonus retailers ride on a
 * Plus/Pro plan, but a plan sold for the retailer itself (e.g.
 * dollar_general_basic, or one an admin added) carries one local store slot,
 * the same as an admin access grant does on the API.
 */
export function getRetailerStoreLimit(retailers: string[], retailer: string): number {
  const limit = getPlanRetailerStoreLimit(retailers, retailer);
  if (limit > 0 || PLUS_ONLY_LOCAL_RETAILERS.has(retailer)) return limit;
  return hasDirectRetailerPlan(retailers, retailer) ? 1 : 0;
}

function getPlanRetailerStoreLimit(retailers: string[], retailer: string): number {
  if (retailer === "home_depot") {
    if (retailers.find((r) => r.includes("home_depot_pro"))) return 5;
    if (retailers.find((r) => r.includes("home_depot_plus"))) return 3;
    if (retailers.find((r) => r.includes("home_depot"))) return 1;
    return 0;
  }

  if (retailer === "dollar_general") {
    if (hasProPlan(retailers)) return 5;
    if (hasPlusPlan(retailers)) return PLUS_BONUS_STORE_LIMIT;
    return 0;
  }

  if (retailer === "target") {
    if (hasProPlan(retailers)) {
      const hasWalmartPro = retailers.some((r) => r.includes("walmart_pro") || r.includes("walmart_enterprise"));
      const hasHomeDepotPro = retailers.some((r) => r.includes("home_depot_pro"));
      if (hasWalmartPro || hasHomeDepotPro) return 5;
    }
    if (hasPlusPlan(retailers)) return 2;
    return 0;
  }

  if (retailer === "walmart") {
    if (retailers.find((r) => r.includes("walmart_enterprise"))) return 10;
    if (retailers.find((r) => r.includes("walmart_pro"))) return 7;
    if (retailers.find((r) => r.includes("walmart_starter"))) return 5;
    if (retailers.find((r) => r.includes("walmart_plus"))) return 3;
    if (retailers.find((r) => r.includes("walmart_basic"))) return 1;
    return 0;
  }

  if (retailer === "bestbuy") {
    if (hasProPlan(retailers)) return 3;
    if (hasPlusPlan(retailers)) return PLUS_BONUS_STORE_LIMIT;
    return 0;
  }

  if (retailer === "lowes") {
    if (hasProPlan(retailers)) return 5;
    if (hasPlusPlan(retailers)) return PLUS_BONUS_STORE_LIMIT;
    return 0;
  }

  if (retailer === "tractor_supply" || retailer === "northern_tool" || retailer === "samsclub") {
    return hasPlusOrProPlan(retailers) ? 5 : 0;
  }

  if (retailer === "costco") {
    if (hasProPlan(retailers)) return 3;
    if (hasPlusPlan(retailers)) return PLUS_BONUS_STORE_LIMIT;
    return 0;
  }

  return 0;
}
